import React, { useState, useMemo } from 'react';
import { Eye, Download, Trash2, CheckSquare, Square, Filter, Search, Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import { formatPrice } from '../utils/helpers';

const statusOptions = [
  { value: '', label: 'Összes státusz' },
  { value: 'pending', label: 'Függőben' },
  { value: 'paid', label: 'Fizetve' },
  { value: 'processing', label: 'Feldolgozás alatt' },
  { value: 'shipped', label: 'Kiszállítva' },
  { value: 'delivered', label: 'Kézbesítve' },
  { value: 'cancelled', label: 'Törölve' }
];

const statusColors = {
  pending: 'bg-yellow-500/20 text-yellow-400',
  paid: 'bg-cyan-500/20 text-cyan-400',
  processing: 'bg-purple-500/20 text-purple-400',
  shipped: 'bg-green-500/20 text-green-400',
  delivered: 'bg-green-500/20 text-green-400',
  cancelled: 'bg-red-500/20 text-red-400'
};

const rowColors = {
  pending: { borderColor: '#facc15', bgColor: 'rgba(250, 204, 21, 0.1)' },      // yellow
  paid: { borderColor: '#22d3ee', bgColor: 'rgba(34, 211, 238, 0.1)' },         // cyan/turquoise
  processing: { borderColor: '#a855f7', bgColor: 'rgba(168, 85, 247, 0.1)' },   // purple
  shipped: { borderColor: '#22c55e', bgColor: 'rgba(34, 197, 94, 0.1)' },       // green
  delivered: { borderColor: '#22c55e', bgColor: 'rgba(34, 197, 94, 0.1)' },     // green
  cancelled: { borderColor: '#ef4444', bgColor: 'rgba(239, 68, 68, 0.1)' }      // red
};

const statusNames = {
  pending: 'Függőben',
  paid: 'Fizetve',
  processing: 'Feldolgozás alatt',
  shipped: 'Kiszállítva',
  delivered: 'Kézbesítve',
  cancelled: 'Törölve'
};

const paymentMethodNames = {
  transfer: 'Banki átutalás',
  barion: 'Barion',
  cash: 'Utánvét',
  simplepay: 'SimplePay'
};

const OrdersManager = ({ orders, onUpdateStatus, onOpenDetail, onDeleteOrder, addToast }) => {
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Sorting
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Selection
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState('');

  // Filter and sort orders
  const filteredOrders = useMemo(() => {
    let result = [...orders];
    
    // Status filter
    if (statusFilter) {
      result = result.filter(order => order.status === statusFilter);
    }
    
    // Search filter (email, order number, name)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(order => 
        order.order_number?.toLowerCase().includes(query) ||
        order.guest_email?.toLowerCase().includes(query) ||
        order.shipping_address?.name?.toLowerCase().includes(query)
      );
    }
    
    // Date filter
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      result = result.filter(order => new Date(order.created_at) >= fromDate);
    }
    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59);
      result = result.filter(order => new Date(order.created_at) <= toDate);
    }
    
    // Sort
    result.sort((a, b) => {
      let aVal, bVal;
      if (sortBy === 'created_at') {
        aVal = new Date(a.created_at);
        bVal = new Date(b.created_at);
      } else if (sortBy === 'total') {
        aVal = a.total;
        bVal = b.total;
      } else if (sortBy === 'status') {
        aVal = a.status;
        bVal = b.status;
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
    
    return result;
  }, [orders, statusFilter, searchQuery, dateFrom, dateTo, sortBy, sortOrder]);

  // Selection handlers
  const toggleSelectOrder = (orderId) => {
    const newSelected = new Set(selectedOrders);
    if (newSelected.has(orderId)) {
      newSelected.delete(orderId);
    } else {
      newSelected.add(orderId);
    }
    setSelectedOrders(newSelected);
  };

  const selectAllOrders = () => {
    if (selectedOrders.size === filteredOrders.length) {
      setSelectedOrders(new Set());
    } else {
      setSelectedOrders(new Set(filteredOrders.map(o => o.id)));
    }
  };

  // Bulk actions
  const handleBulkStatusChange = async () => {
    if (!bulkStatus || selectedOrders.size === 0) return;
    
    for (const orderId of selectedOrders) {
      await onUpdateStatus(orderId, bulkStatus);
    }
    addToast(`${selectedOrders.size} rendelés státusza frissítve!`, 'success');
    setSelectedOrders(new Set());
    setBulkStatus('');
  };

  const handleBulkDelete = async () => {
    if (selectedOrders.size === 0) return;
    if (!window.confirm(`Biztosan törölni szeretnéd a kijelölt ${selectedOrders.size} rendelést?`)) return;
    
    for (const orderId of selectedOrders) {
      await onDeleteOrder(orderId);
    }
    addToast(`${selectedOrders.size} rendelés törölve!`, 'success');
    setSelectedOrders(new Set());
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Rendelésszám', 'Dátum', 'Vásárló email', 'Vásárló név', 'Termékek', 'Szolgáltatások', 'Részösszeg', 'Szállítás', 'Végösszeg', 'Fizetési mód', 'Státusz', 'Szállítási cím'];
    
    const rows = filteredOrders.map(order => {
      const products = order.items?.map(i => `${i.name} x${i.quantity}`).join('; ') || '';
      const services = order.service_items?.map(i => `${i.name} x${i.quantity}`).join('; ') || '';
      const address = order.shipping_address 
        ? `${order.shipping_address.name}, ${order.shipping_address.address}, ${order.shipping_address.city} ${order.shipping_address.zip}`
        : 'Nincs';
      
      return [
        order.order_number,
        new Date(order.created_at).toLocaleDateString('hu-HU'),
        order.guest_email || '',
        order.shipping_address?.name || '',
        products,
        services,
        order.subtotal,
        order.shipping_cost,
        order.total,
        paymentMethodNames[order.payment_method] || order.payment_method,
        statusNames[order.status] || order.status,
        address
      ];
    });
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `rendelesek_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    
    addToast('Rendelések exportálva!', 'success');
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <h2 className="text-2xl font-semibold">Rendelések kezelése</h2>
        <div className="flex gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition ${showFilters ? 'bg-yellow-200 text-black' : 'glass hover:bg-white/10'}`}
          >
            <Filter className="w-4 h-4" />
            Szűrők
          </button>
          <button
            onClick={exportToCSV}
            className="px-4 py-2 glass rounded-lg flex items-center gap-2 hover:bg-white/10 transition"
          >
            <Download className="w-4 h-4" />
            CSV Export
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="glass rounded-xl p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">Keresés</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rendelésszám, email, név..."
                  className="w-full pl-10 pr-4 py-2 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
                />
              </div>
            </div>

            {/* Status filter */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">Státusz</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
              >
                {statusOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Date from */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">Dátum (-tól)</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full px-4 py-2 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
              />
            </div>

            {/* Date to */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">Dátum (-ig)</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full px-4 py-2 bg-black/30 border border-white/10 rounded-lg focus:border-yellow-200/50 focus:outline-none text-white"
              />
            </div>
          </div>

          {/* Clear filters */}
          <div className="flex justify-end">
            <button
              onClick={() => {
                setStatusFilter('');
                setSearchQuery('');
                setDateFrom('');
                setDateTo('');
              }}
              className="text-sm text-gray-400 hover:text-white transition"
            >
              Szűrők törlése
            </button>
          </div>
        </div>
      )}

      {/* Bulk actions */}
      {selectedOrders.size > 0 && (
        <div className="glass rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-yellow-200/10 border-yellow-200/30">
          <span className="text-yellow-200 font-semibold">
            {selectedOrders.size} rendelés kijelölve
          </span>
          <div className="flex flex-wrap gap-2">
            <select
              value={bulkStatus}
              onChange={(e) => setBulkStatus(e.target.value)}
              className="px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-sm focus:outline-none"
            >
              <option value="">Státusz változtatás...</option>
              {statusOptions.filter(o => o.value).map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <button
              onClick={handleBulkStatusChange}
              disabled={!bulkStatus}
              className="px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition text-sm"
            >
              Alkalmazás
            </button>
            <button
              onClick={handleBulkDelete}
              className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition text-sm flex items-center gap-1"
            >
              <Trash2 className="w-4 h-4" />
              Törlés
            </button>
            <button
              onClick={() => setSelectedOrders(new Set())}
              className="px-4 py-2 glass hover:bg-white/10 rounded-lg transition text-sm"
            >
              Kijelölés törlése
            </button>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="flex flex-wrap gap-4 text-sm text-gray-400">
        <span>Összesen: <strong className="text-white">{filteredOrders.length}</strong> rendelés</span>
        <span>Összérték: <strong className="text-yellow-200">{formatPrice(filteredOrders.reduce((sum, o) => sum + o.total, 0))}</strong></span>
      </div>

      {/* Table header */}
      <div className="hidden lg:grid lg:grid-cols-12 gap-4 px-6 py-3 glass rounded-xl text-sm font-semibold text-gray-400">
        <div className="col-span-1 flex items-center">
          <button onClick={selectAllOrders} className="hover:text-white transition">
            {selectedOrders.size === filteredOrders.length && filteredOrders.length > 0 ? (
              <CheckSquare className="w-5 h-5 text-yellow-200" />
            ) : (
              <Square className="w-5 h-5" />
            )}
          </button>
        </div>
        <button onClick={() => handleSort('created_at')} className="col-span-2 flex items-center gap-1 hover:text-white transition">
          Dátum <SortIcon field="created_at" />
        </button>
        <div className="col-span-2">Rendelésszám</div>
        <div className="col-span-2">Vásárló</div>
        <div className="col-span-1">Tételek</div>
        <button onClick={() => handleSort('total')} className="col-span-1 flex items-center gap-1 hover:text-white transition">
          Összeg <SortIcon field="total" />
        </button>
        <button onClick={() => handleSort('status')} className="col-span-1 flex items-center gap-1 hover:text-white transition">
          Státusz <SortIcon field="status" />
        </button>
        <div className="col-span-2 text-right">Műveletek</div>
      </div>

      {/* Orders list */}
      {filteredOrders.length === 0 ? (
        <div className="glass rounded-xl p-12 text-center text-gray-400">
          <p>Nincs találat a megadott szűrőkkel.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredOrders.map((order) => (
            <div 
              key={order.id} 
              className={`glass rounded-xl p-4 lg:p-6 transition ${rowColors[order.status] || ''} ${selectedOrders.has(order.id) ? 'ring-2 ring-yellow-200/50' : ''}`}
            >
              {/* Desktop view */}
              <div className="hidden lg:grid lg:grid-cols-12 gap-4 items-center">
                {/* Checkbox */}
                <div className="col-span-1">
                  <button onClick={() => toggleSelectOrder(order.id)} className="hover:text-yellow-200 transition">
                    {selectedOrders.has(order.id) ? (
                      <CheckSquare className="w-5 h-5 text-yellow-200" />
                    ) : (
                      <Square className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                </div>

                {/* Date */}
                <div className="col-span-2">
                  <p className="text-white">{new Date(order.created_at).toLocaleDateString('hu-HU')}</p>
                  <p className="text-xs text-gray-400">{new Date(order.created_at).toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' })}</p>
                </div>

                {/* Order number */}
                <div className="col-span-2">
                  <p className="font-semibold text-white">#{order.order_number}</p>
                  <p className="text-xs text-gray-400">{order.user_id ? 'Regisztrált' : 'Vendég'}</p>
                </div>

                {/* Customer */}
                <div className="col-span-2">
                  <p className="text-white truncate">{order.shipping_address?.name || '-'}</p>
                  <p className="text-xs text-gray-400 truncate">{order.guest_email || '-'}</p>
                </div>

                {/* Items count */}
                <div className="col-span-1">
                  <p className="text-white">{(order.items?.length || 0) + (order.service_items?.length || 0)} tétel</p>
                  <p className="text-xs text-gray-400">
                    {order.items?.length || 0} termék, {order.service_items?.length || 0} szolg.
                  </p>
                </div>

                {/* Total */}
                <div className="col-span-1">
                  <p className="font-bold text-yellow-200">{formatPrice(order.total)}</p>
                  <p className="text-xs text-gray-400">{paymentMethodNames[order.payment_method] || '-'}</p>
                </div>

                {/* Status */}
                <div className="col-span-1">
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${statusColors[order.status] || 'bg-gray-500/20 text-gray-400'}`}>
                    {statusNames[order.status] || order.status}
                  </span>
                </div>

                {/* Actions */}
                <div className="col-span-2 flex justify-end gap-2">
                  <button
                    onClick={() => onOpenDetail(order)}
                    className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition text-sm flex items-center gap-1"
                  >
                    <Eye className="w-4 h-4" />
                    Megnyitás
                  </button>
                  <select
                    value={order.status}
                    onChange={(e) => onUpdateStatus(order.id, e.target.value)}
                    className="px-2 py-1.5 bg-black/30 border border-white/10 rounded-lg text-sm focus:outline-none focus:border-yellow-200/50"
                  >
                    {statusOptions.filter(o => o.value).map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Mobile view */}
              <div className="lg:hidden space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <button onClick={() => toggleSelectOrder(order.id)}>
                      {selectedOrders.has(order.id) ? (
                        <CheckSquare className="w-5 h-5 text-yellow-200" />
                      ) : (
                        <Square className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                    <div>
                      <p className="font-semibold">#{order.order_number}</p>
                      <p className="text-xs text-gray-400">
                        {new Date(order.created_at).toLocaleDateString('hu-HU')} - {order.user_id ? 'Regisztrált' : 'Vendég'}
                      </p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusColors[order.status]}`}>
                    {statusNames[order.status]}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-gray-400">Vásárló</p>
                    <p className="text-white">{order.shipping_address?.name || order.guest_email || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Összeg</p>
                    <p className="text-yellow-200 font-bold">{formatPrice(order.total)}</p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => onOpenDetail(order)}
                    className="flex-1 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition text-sm flex items-center justify-center gap-1"
                  >
                    <Eye className="w-4 h-4" />
                    Megnyitás
                  </button>
                  <select
                    value={order.status}
                    onChange={(e) => onUpdateStatus(order.id, e.target.value)}
                    className="flex-1 px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-sm focus:outline-none"
                  >
                    {statusOptions.filter(o => o.value).map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrdersManager;
